import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { ShieldCheck, Users, Building2, UserCog } from "lucide-react";
import { api } from "../lib/api";

export const PermissionsOverview = ({ onManage, refreshKey }) => {
  const [users, setUsers] = useState([]);
  const [branchCount, setBranchCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [us, br] = await Promise.all([api.listUsers(), api.branchNames()]);
      setUsers(us);
      setBranchCount(br.length);
    } catch {
      /* silent — manager-only panel */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  if (loading || users.length === 0) return null;

  const staff = users.filter((u) => u.role !== "manager");

  return (
    <motion.div
      data-testid="permissions-overview"
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
      className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-xl bg-blue-50 flex items-center justify-center">
            <UserCog className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <p className="font-heading font-bold text-slate-900">توزيع الصلاحيات</p>
            <p className="text-xs text-slate-400">نظرة سريعة على المستخدمين والفروع التي يغطيها كل منهم</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" data-testid="overview-users-count" className="gap-1 rounded-full border-slate-200 text-slate-600">
            <Users className="h-3.5 w-3.5" /> {users.length} مستخدم
          </Badge>
          <Badge variant="outline" data-testid="overview-branches-count" className="gap-1 rounded-full border-slate-200 text-slate-600">
            <Building2 className="h-3.5 w-3.5" /> {branchCount} فرع
          </Badge>
          <Button size="sm" variant="outline" data-testid="overview-manage-button" className="gap-1.5" onClick={onManage}>
            <UserCog className="h-4 w-4" /> إدارة
          </Button>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {users.map((u) => (
          <div key={u.user_id || u.email} data-testid={`overview-user-${u.user_id || u.email}`}
            className="rounded-xl border border-slate-100 bg-slate-50/50 p-3">
            <div className="flex items-center justify-between gap-2">
              <p className="font-semibold text-slate-800 text-sm truncate">{u.name || u.email}</p>
              {u.role === "manager" ? (
                <Badge className="shrink-0 gap-1 bg-blue-600 hover:bg-blue-600 text-[11px]">
                  <ShieldCheck className="h-3 w-3" /> كل الفروع
                </Badge>
              ) : (
                <Badge variant="outline" className="shrink-0 text-[11px] text-slate-600">
                  {(u.allowed_branches || []).length} فرع
                </Badge>
              )}
            </div>
            {u.role !== "manager" && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {(u.allowed_branches || []).length > 0 ? (
                  (u.allowed_branches || []).map((b) => (
                    <span key={b} className="rounded-full bg-blue-50 border border-blue-100 text-blue-700 text-[11px] px-2 py-0.5">{b}</span>
                  ))
                ) : (
                  <span className="text-[11px] text-amber-600">لا توجد فروع مصرّح بها</span>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </motion.div>
  );
};
